import { JSX } from 'react'

import { LandmarkIcon } from 'lucide-react'

import {
  CurrencyAmount,
  NetworkLogo,
  TokenBalance,
  tokenBalanceFormatters,
} from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import { ListItemProps } from '~/components/component-library/List/types'
import { TokenDisplay } from '~/components/component-library/TokenDisplay'
import {
  ApprovalStateChangeItem,
  ERC20StateChangeItem,
  NFTCollectionStateChangeItem,
  OfframpDisplayData,
  StateChangeItem,
} from '~/components/txn-display/state-change/types'
import { shortenAddress } from '~/utils/addresses'
import { plural } from '~/utils/display'
import { getNumberFromBigNumber } from '~/utils/numbers'
import { calculateOfframpNetAmount, ceilToCents } from '~/utils/offramp'
import { cn } from '~/utils/tailwind-utils'

const BankAccountDisplay = ({
  bankName,
  liquidationAddress,
}: {
  bankName: string
  liquidationAddress: string
}) => {
  return (
    <div className="flex w-full items-center gap-2 text-base">
      <div className="flex size-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700">
        <LandmarkIcon className="size-5" />
      </div>
      <div className="flex w-full min-w-0 flex-col items-start justify-center gap-0.5 leading-tight">
        <div className="flex h-6 w-full min-w-0 items-center truncate leading-6 text-primary">
          {bankName}
        </div>
        <div className="h-4 flex-shrink-0 text-sm leading-4 text-secondary">
          {shortenAddress(liquidationAddress)}
        </div>
      </div>
    </div>
  )
}

export const buildStateChangeListItem = (
  item: StateChangeItem,
  dataRefreshing?: boolean,
  offrampData?: OfframpDisplayData,
): ListItemProps => {
  // Check if this is an offramp inflow item (recipient is the liquidation address)
  const isOfframpInflow =
    offrampData &&
    item.address.toLowerCase() === offrampData.liquidationAddress.toLowerCase()

  return {
    key: item.id,
    content: isOfframpInflow ? (
      <BankAccountDisplay
        bankName={offrampData.bankName}
        liquidationAddress={offrampData.liquidationAddress}
      />
    ) : (
      <AddressDisplay
        address={item.address}
        chainId={item.chainId}
        layout="two-line"
      />
    ),
    meta: buildMetaContent(item, dataRefreshing, offrampData),
  }
}

const buildApprovalMeta = (item: ApprovalStateChangeItem): JSX.Element => {
  const formattedAmount = tokenBalanceFormatters.formatAbbreviatedTokenAmount(
    getNumberFromBigNumber(item.amount, item.tokenMetadata?.decimals),
    { address: item.token, chainId: item.chainId },
    { removeTrailingZeros: true },
  )

  return (
    <div className="flex flex-col items-end justify-center gap-1">
      <span className="h-6 text-base leading-6 text-primary">
        Approve spend
      </span>
      <span className="h-4 text-sm leading-4 text-secondary">
        {item.isUnlimited ? 'Unlimited' : formattedAmount}{' '}
        <TokenDisplay
          address={item.token}
          chainId={item.chainId}
          layout="inline-text"
        />
      </span>
    </div>
  )
}

const buildNFTMeta = (nftItem: NFTCollectionStateChangeItem): JSX.Element => {
  const absoluteAmount = nftItem.amount >= 0n ? nftItem.amount : -nftItem.amount
  const count = absoluteAmount.toString()
  const collectionName =
    nftItem.collectionMetadata?.collectionName ||
    nftItem.tokenMetadata?.symbol ||
    shortenAddress(nftItem.token)

  return (
    <div className="flex flex-col items-end justify-center gap-0.5">
      <span
        className={cn(
          'h-6 leading-6',
          nftItem.direction === 'inflow' ? 'text-green-600' : 'text-primary',
        )}
      >
        {nftItem.direction === 'inflow' ? '+' : '-'}
        {count} {plural('NFT', Number(absoluteAmount))}
      </span>
      <span className="flex h-4 max-w-40 items-center gap-1 truncate text-sm leading-4 text-secondary">
        <span className="truncate">
          {collectionName} #{nftItem.tokenId.toString()}
        </span>
        <div className="flex-shrink-0 [&_div]:!ring-white [&_div]:dark:!ring-gray-900">
          <NetworkLogo chainId={nftItem.chainId} size="sm" />
        </div>
      </span>
    </div>
  )
}

// Shared by review dialogs and the send form so copy and rounding can't drift.
export const formatOfframpFeeFooter = ({
  processingFee,
  processingFeeRate,
  currency,
}: Pick<
  OfframpDisplayData,
  'processingFee' | 'processingFeeRate' | 'currency'
>): string => {
  const feePercent = (processingFeeRate * 100).toFixed(2)
  const currencySymbol = currency === 'EUR' ? '€' : '$'
  const feeAmount =
    processingFee > 0
      ? ` (${currencySymbol}${ceilToCents(processingFee).toFixed(2)})`
      : ''
  return `Stripe charges a ${feePercent}% fee${feeAmount} on bank transfers`
}

const OfframpAmountDisplay = ({
  grossAmount,
  feeRate,
  currency,
  isLoading,
}: {
  grossAmount: number
  feeRate: number
  currency: 'USD' | 'EUR'
  isLoading?: boolean
}) => {
  const netAmount = calculateOfframpNetAmount(grossAmount, feeRate)

  return (
    <div className="flex flex-col items-end justify-center gap-0.5">
      <CurrencyAmount
        layout="standard"
        value={netAmount}
        currency={currency}
        direction="inflow"
        isLoading={isLoading}
      />
      {/* Spacer for two-line layout alignment */}
      <div className="h-4 leading-4" />
    </div>
  )
}

const buildERC20Meta = (
  item: ERC20StateChangeItem,
  dataRefreshing?: boolean,
  offrampData?: OfframpDisplayData,
): JSX.Element => {
  const absoluteAmount = item.amount >= 0n ? item.amount : -item.amount

  // Check if this is an offramp inflow to adjust for fees
  const isOfframpInflow =
    offrampData &&
    item.address.toLowerCase() === offrampData.liquidationAddress.toLowerCase()

  // For offramp inflows, show currency amount ($ or €) instead of token balance
  if (isOfframpInflow) {
    // Default to 6 decimals (USDC/EURC)
    const decimals = item.tokenMetadata?.decimals ?? 6
    const grossAmount = getNumberFromBigNumber(absoluteAmount, decimals)

    return (
      <OfframpAmountDisplay
        grossAmount={grossAmount}
        feeRate={offrampData.processingFeeRate}
        currency={offrampData.currency}
        isLoading={dataRefreshing}
      />
    )
  }

  return (
    <TokenBalance
      address={item.token}
      chainId={item.chainId}
      tokenAmount={absoluteAmount}
      layout="standard"
      direction={item.direction}
      size="base"
      showNetworkIcon
      usdValue={item.usdValue ?? undefined}
      isLoadingUsdValue={dataRefreshing}
    />
  )
}

const buildMetaContent = (
  item: StateChangeItem,
  dataRefreshing?: boolean,
  offrampData?: OfframpDisplayData,
): JSX.Element => {
  switch (item.state_change_type) {
    case 'approval':
      return buildApprovalMeta(item)
    case 'erc-721':
    case 'erc-1155':
      return buildNFTMeta(item)
    case 'erc-20':
      return buildERC20Meta(item, dataRefreshing, offrampData)
    default:
      return <span className="text-base text-primary">Unknown</span>
  }
}
